import { useNavigate } from 'react-router-dom';

import { copy } from '../../config/copy';
import { useFavorites } from '../../stores/favorites';
import { useSession } from '../../stores/session';

/**
 * `sm` and `md` size the *button*, for a heart standing on its own in a row or
 * a card. `button` sizes it to a `<Button>` instead: the glyph stays the same
 * and only the box around it shrinks, so a caller that supplies its own padding
 * and border does not end up adding height twice.
 */
type Size = 'sm' | 'md' | 'button';

interface Props {
  trackId: number;
  /** What the server last said. The store's overlay wins if it has an opinion. */
  favorited?: boolean;
  likeCount?: number;
  showCount?: boolean;
  size?: Size;
  className?: string;
}

/**
 * The heart. One component, used in the track list, the track page and the dock.
 *
 * All three read the same store, so favouriting in any of them updates the
 * other two - including the dock, which outlives the page the click happened
 * on. See `stores/favorites.ts` for why that is an overlay rather than a cache.
 *
 * Signed out, this is not disabled. A disabled heart tells somebody the feature
 * is broken; sending them to sign in tells them what to do, and is the moment
 * they are most likely to actually want an account.
 */
export function FavoriteButton({
  trackId,
  favorited,
  likeCount = 0,
  showCount = false,
  size = 'md',
  className = '',
}: Props) {
  const navigate = useNavigate();
  const user = useSession((state) => state.user);

  // Subscribed to the maps rather than calling the resolvers, so this component
  // re-renders when its own track changes. A selector returning a function
  // would read the right value once and never update.
  const overlay = useFavorites((state) => state.overlay[trackId]);
  const overlayCount = useFavorites((state) => state.counts[trackId]);
  const pending = useFavorites((state) => state.pending[trackId] ?? false);
  const toggle = useFavorites((state) => state.toggle);

  const isFavorited = overlay ?? favorited ?? false;
  const count = overlayCount ?? likeCount;

  /*
   * `button` is a 20px box, which is the line-height of `text-sm` - so with the
   * `px-4 py-2.5` and the border a caller pairs it with, the result is exactly
   * the height of a `<Button>` beside it. The larger sizes carry their own
   * height because nothing else is giving them any.
   */
  const dimension = { sm: 'h-8 w-8', md: 'h-9 w-9', button: 'h-5 w-5' }[size];
  const glyph = size === 'sm' ? 'h-4 w-4' : 'h-[18px] w-[18px]';

  return (
    <button
      type="button"
      aria-pressed={isFavorited}
      aria-label={isFavorited ? copy['action.unlike'] : copy['action.like']}
      data-favorite-button={trackId}
      data-favorited={isFavorited ? 'true' : 'false'}
      onClick={(event) => {
        // Hearts live inside links and rows that are themselves clickable.
        event.preventDefault();
        event.stopPropagation();

        if (!user) {
          navigate('/login');

          return;
        }

        void toggle(trackId, isFavorited, count);
      }}
      className={[
        'group inline-flex items-center gap-1.5 rounded-[var(--radius-pill)]',
        'transition-colors disabled:opacity-60',
        showCount ? 'px-2' : '',
        className,
      ].join(' ')}
      disabled={pending}
    >
      <span
        className={[
          'inline-flex items-center justify-center rounded-[var(--radius-pill)] transition-colors',
          dimension,
          isFavorited ? 'text-hot' : 'text-soft group-hover:text-ink',
        ].join(' ')}
      >
        <svg
          viewBox="0 0 20 20"
          className={glyph}
          // Filled when favourited, outlined when not. The state has to be
          // legible without colour - a red-versus-grey heart is invisible to
          // the most common form of colour blindness.
          fill={isFavorited ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth="1.6"
          aria-hidden="true"
        >
          <path d="M10 17s-6.5-4.2-6.5-8.3A3.7 3.7 0 0 1 10 6.2a3.7 3.7 0 0 1 6.5 2.5C16.5 12.8 10 17 10 17Z" />
        </svg>
      </span>

      {showCount && (
        <span className="meta-caps tabular-nums" data-favorite-count={trackId}>
          {count.toLocaleString()}
        </span>
      )}
    </button>
  );
}
